// Owner BI — Guest sentiment radar: AI-grouped issues / praise from chat, contact, reviews, surveys, service notes, voice notes.
import React, { useEffect, useState } from 'react';
import { Radar, RefreshCw, Wrench, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { apiCall } from './_shared';

const SEV = { 1: 'bg-slate-100 text-slate-700', 2: 'bg-sky-100 text-sky-800', 3: 'bg-amber-100 text-amber-900', 4: 'bg-orange-100 text-orange-900', 5: 'bg-rose-100 text-rose-900' };

const SentimentRadarCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [msg, setMsg] = useState('');

  const load = async () => { try { setData(await apiCall('/api/admin/sentiment-radar')); } catch (e) { setMsg(e.message); } };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall(`/api/admin/sentiment-radar/run?days=${days}`, { method: 'POST' });
      setMsg(r.error === 'not_enough_data' ? `Only ${r.items} guest texts in the last ${days} days — not enough to analyse yet.` : r.error ? `Failed: ${r.error}` : `Analysed ${r.items} texts → ${r.topics.length} topics.`);
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const ticket = async (idx) => {
    try {
      const r = await apiCall(`/api/admin/sentiment-radar/${data.latest.id}/topics/${idx}/ticket`, { method: 'POST', body: JSON.stringify({}) });
      setMsg(r.existing ? 'A maintenance task for this topic already exists.' : 'Maintenance task created — see Housekeeping → Maintenance.');
      await load();
    } catch (e) { setMsg(e.message); }
  };

  const latest = data?.latest;
  const Delta = ({ d }) => (d == null ? <Minus className="w-3 h-3 text-slate-300 inline" /> : d > 0 ? <ArrowUpRight className="w-3.5 h-3.5 text-rose-600 inline" /> : d < 0 ? <ArrowDownRight className="w-3.5 h-3.5 text-emerald-600 inline" /> : <Minus className="w-3 h-3 text-slate-400 inline" />);

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="sentiment-radar-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-slate-900 inline-flex items-center gap-2"><Radar className="w-4 h-4 text-orange-600" /> Guest sentiment radar</h3>
        <div className="flex items-center gap-2 text-xs">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border rounded px-2 py-1" data-testid="sentiment-days">{[7, 14, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>last {d} days</option>)}</select>
          <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1 border rounded px-2 py-1 hover:bg-slate-50 disabled:opacity-50" data-testid="sentiment-run"><RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Analyse now</button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Every Monday 08:00 the AI reads chat questions, contact messages, reviews, in-room surveys, room-service notes and voice-note transcripts and groups them into trending topics. Issues can become a maintenance task with one tap.</p>
      {msg && <p className="text-xs text-slate-700" data-testid="sentiment-msg">{msg}</p>}
      {!latest && <p className="text-xs text-slate-500" data-testid="sentiment-empty">No analysis yet — press "Analyse now".</p>}
      {latest && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="sentiment-summary">
            <span className="text-slate-500">{new Date(latest.at).toLocaleString('en-GB')} · {latest.items} texts</span>
            {latest.net != null && <span className={`px-2 py-0.5 rounded-full font-semibold ${latest.net >= 40 ? 'bg-emerald-100 text-emerald-800' : latest.net >= 0 ? 'bg-amber-100 text-amber-900' : 'bg-rose-100 text-rose-900'}`} data-testid="sentiment-net">Net mood {latest.net > 0 ? '+' : ''}{latest.net}</span>}
            {Object.entries(latest.sources || {}).map(([k, v]) => <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{k} {v}</span>)}
          </div>
          {latest.mood && <p className="text-sm text-slate-800 italic" data-testid="sentiment-mood">“{latest.mood}”</p>}
          {latest.error === 'not_enough_data' && <p className="text-xs text-amber-700">Not enough guest texts in that period.</p>}
          <ul className="divide-y" data-testid="sentiment-topics">
            {(latest.topics || []).map((t, i) => (
              <li key={i} className="py-2.5 flex flex-wrap items-start gap-3" data-testid={`sentiment-topic-${i}`}>
                <span className={`mt-0.5 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${t.kind === 'issue' ? SEV[t.severity] || SEV[3] : 'bg-emerald-100 text-emerald-800'}`}>{t.kind === 'issue' ? `issue · sev ${t.severity}` : 'praise'}</span>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-medium text-slate-900">{t.label} <span className="text-xs text-slate-500">×{t.count} · {t.area}</span></div>
                  {t.examples?.length > 0 && <div className="text-xs text-slate-500 mt-0.5">{t.examples.map((e, j) => <span key={j} className="block truncate">“{e}”</span>)}</div>}
                  {t.action && <div className="text-xs text-slate-700 mt-1">→ {t.action}</div>}
                </div>
                {t.kind === 'issue' && (t.ticket_id
                  ? <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> task created</span>
                  : <button type="button" onClick={() => ticket(i)} className="text-xs inline-flex items-center gap-1 border rounded px-2 py-1 hover:bg-slate-50" data-testid={`sentiment-ticket-${i}`}><Wrench className="w-3.5 h-3.5" /> Create maintenance task</button>)}
              </li>
            ))}
          </ul>
          {data.trend?.length > 0 && data.runs?.length > 1 && (
            <details data-testid="sentiment-trend-toggle">
              <summary className="text-xs text-slate-600 cursor-pointer">Trend across the last {data.runs.length} analyses</summary>
              <table className="w-full text-xs mt-2" data-testid="sentiment-trend">
                <thead><tr className="text-left text-slate-500"><th className="py-1">Topic</th><th>Kind</th><th className="text-right">Counts (old → new)</th><th className="text-right">Δ</th></tr></thead>
                <tbody>{data.trend.slice(0, 15).map((r) => (
                  <tr key={r.label} className="border-t"><td className="py-1">{r.label}</td><td className="text-slate-500">{r.kind}</td>
                    <td className="text-right"><span className="inline-flex items-end gap-px h-4 align-middle">{r.series.map((v, i) => <i key={i} className={`inline-block w-1.5 rounded-sm ${r.kind === 'issue' ? 'bg-rose-400' : 'bg-emerald-400'}`} style={{ height: `${Math.max(2, (v / Math.max(1, ...r.series)) * 16)}px` }} title={String(v)} />)}</span> <span className="ml-1">{r.series.join(' → ')}</span></td>
                    <td className="text-right"><Delta d={r.delta} /> {r.delta == null ? '' : r.delta > 0 ? `+${r.delta}` : r.delta}</td></tr>
                ))}</tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default SentimentRadarCard;
